import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { PackageCheck } from 'lucide-react';

import { usePendingPickups } from '../hooks/use-pending-pickups';
import { useConfirmPickup } from '../hooks/use-confirm-pickup';
import type { PendingPickup } from '../types/pickup.types';

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/atoms/card';
import { Input } from '@/components/atoms/input';
import { Button } from '@/components/atoms/button';
import { Label } from '@/components/atoms/label';
import { Skeleton } from '@/components/atoms/skeleton';
import { EmptyState } from '@/components/organisms/empty-state';
import { formatRelativeTime } from '@/lib/utils';

const PICKUP_CODE_LENGTH = 6;

interface PickupCardProps {
  pickup: PendingPickup;
}

function PickupCard({ pickup }: PickupCardProps) {
  const { t, i18n } = useTranslation();
  const [code, setCode] = React.useState('');
  const confirmMutation = useConfirmPickup();

  const isCodeComplete = code.length === PICKUP_CODE_LENGTH;
  // Paiement en ligne non encaissé : la remise est refusée par le serveur (80024).
  // On bloque aussi côté UI et on l'explique au vendeur. Le mode `in_store` encaisse
  // au comptoir, donc « en attente » y est normal (pas de blocage).
  const unpaidOnline = pickup.paymentMethod !== 'in_store' && !pickup.isPaid;

  const handleConfirm = () => {
    if (!isCodeComplete) return;
    confirmMutation.mutate({ orderId: pickup.orderId, code });
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2">
          <CardTitle className="text-base">{pickup.customerName}</CardTitle>
          <span
            className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${
              pickup.isPaid
                ? 'bg-green-100 text-green-700'
                : 'bg-amber-100 text-amber-700'
            }`}
          >
            {pickup.isPaid
              ? t('orders.pickupConfirmation.paid')
              : t('orders.pickupConfirmation.paymentPending')}
          </span>
        </div>
        <CardDescription>
          #{pickup.orderId} · {formatRelativeTime(pickup.createdAt, i18n.language)}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {pickup.products.length > 0 && (
          <ul className="space-y-1 text-sm text-muted-foreground">
            {pickup.products.map((product) => (
              <li key={product.refId}>
                {product.quantity}× {product.name}
              </li>
            ))}
          </ul>
        )}

        {unpaidOnline && (
          <p className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800">
            {t('orders.pickupConfirmation.unpaidWarning')}
          </p>
        )}

        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="flex-1 space-y-1.5">
            <Label htmlFor={`pickup-code-${pickup.orderId}`}>
              {t('orders.pickupConfirmation.codeLabel')}
            </Label>
            <Input
              id={`pickup-code-${pickup.orderId}`}
              inputMode="numeric"
              pattern="[0-9]*"
              autoComplete="one-time-code"
              maxLength={PICKUP_CODE_LENGTH}
              disabled={unpaidOnline}
              placeholder={t('orders.pickupConfirmation.codePlaceholder')}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, PICKUP_CODE_LENGTH))}
              className="font-mono text-lg tracking-[0.3em]"
            />
          </div>
          <Button
            onClick={handleConfirm}
            disabled={!isCodeComplete || confirmMutation.isPending || unpaidOnline}
            loading={confirmMutation.isPending}
          >
            <PackageCheck />
            {t('orders.pickupConfirmation.confirmButton')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function PickupCardSkeleton() {
  return (
    <Card>
      <CardHeader className="pb-2 space-y-2">
        <Skeleton className="h-5 w-32" />
        <Skeleton className="h-4 w-40" />
      </CardHeader>
      <CardContent className="space-y-4">
        <Skeleton className="h-4 w-48" />
        <div className="flex gap-3">
          <Skeleton className="h-9 flex-1" />
          <Skeleton className="h-9 w-40" />
        </div>
      </CardContent>
    </Card>
  );
}

export function PickupConfirmation() {
  const { t } = useTranslation();
  const { data: pickups = [], isLoading } = usePendingPickups();

  if (isLoading) {
    return (
      <div className="grid gap-4 md:grid-cols-2">
        <PickupCardSkeleton />
        <PickupCardSkeleton />
      </div>
    );
  }

  if (pickups.length === 0) {
    return (
      <EmptyState
        icon={PackageCheck}
        title={t('orders.pickupConfirmation.emptyTitle')}
        description={t('orders.pickupConfirmation.emptyDescription')}
      />
    );
  }

  return (
    <div className="grid gap-4 md:grid-cols-2">
      {pickups.map((pickup) => (
        <PickupCard key={pickup.orderId} pickup={pickup} />
      ))}
    </div>
  );
}
